import { create } from "zustand";

/**
 * Resource display names for the breadcrumb trail, keyed by pathname.
 *
 * Breadcrumbs are derived purely from the URL, so a detail route like
 * `/storage/6f9619ff-…` would otherwise bottom out on the raw UUID. A detail
 * page publishes its resource's name here once loaded (see
 * `components/ResourceDetail.tsx`), and `components/Breadcrumbs.tsx` renders
 * it — falling back to a shortened id while the name is still unknown.
 *
 * Deliberately not persisted: names are re-published on every visit, and a
 * stale localStorage name after a rename would be worse than the fallback.
 */
interface BreadcrumbState {
  names: Record<string, string>;
  setName: (pathname: string, name: string) => void;
}

/** Cap so a long session can't grow the map without bound. */
const MAX_NAMES = 50;

export const useBreadcrumbStore = create<BreadcrumbState>()((set) => ({
  names: {},
  setName: (pathname, name) =>
    set((state) => {
      if (state.names[pathname] === name) return state;
      const rest = Object.entries(state.names).filter(([p]) => p !== pathname);
      const kept = rest.slice(Math.max(0, rest.length - (MAX_NAMES - 1)));
      return { names: Object.fromEntries([...kept, [pathname, name]]) };
    }),
}));
